import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
    useAccount,
    useChainId,
    useConnect,
    useDisconnect,
    useSwitchChain,
    useReadContract,
    useWriteContract,
    useWaitForTransactionReceipt,
} from "wagmi";
import { parseUnits, formatUnits } from "viem";
import {
    Wallet,
    ShieldAlert,
    ShieldCheck,
    ExternalLink,
    Copy,
    CheckCircle2,
    AlertTriangle,
    Info,
    X,
    ArrowUpRight,
    Loader2,
    ChevronDown,
} from "lucide-react";

import { Reveal } from "@/components/Reveal";
import { AmbientBackground } from "@/components/AmbientBackground";
import { AIMA_CONFIG, AIMA_SALE_ABI, ERC20_ABI } from "@/lib/aima";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* -------- helpers -------- */
const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");
const arbiscanAddr = (a) => `${AIMA_CONFIG.chain.explorer}/address/${a}`;
const arbiscanTx = (h) => `${AIMA_CONFIG.chain.explorer}/tx/${h}`;
const fmt = (v, decimals, digits = 2) => {
    if (v === undefined || v === null) return "—";
    try {
        const n = Number(formatUnits(v, decimals));
        return n.toLocaleString(undefined, { maximumFractionDigits: digits });
    } catch (_) {
        return "—";
    }
};

const CopyChip = ({ value }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(value);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                } catch (_) { /* noop */ }
            }}
            className="inline-flex items-center gap-1.5 text-white/45 hover:text-white transition-colors"
            aria-label="Copy address"
        >
            {copied ? <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> : <Copy className="w-3 h-3" strokeWidth={1.8} />}
        </button>
    );
};

const AddressRow = ({ label, addr, treasury, pendingLabel }) => {
    const isPending = !addr || typeof addr !== "string" || addr.trim() === "";
    return (
        <div
            className={`flex items-center justify-between gap-3 py-3 border-t border-white/[0.06] first:border-t-0 ${
                treasury ? "bg-white/[0.015]" : ""
            }`}
        >
            <div className="flex flex-col">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/45">
                    {label}
                </span>
                {isPending ? (
                    <span className="font-mono text-[11.5px] text-cyan/75 mt-0.5 italic">
                        {pendingLabel}
                    </span>
                ) : (
                    <span className="font-mono text-[11.5px] text-white/75 mt-0.5 break-all">
                        {addr}
                    </span>
                )}
            </div>
            {!isPending && (
                <div className="flex items-center gap-2 shrink-0">
                    <CopyChip value={addr} />
                    <a
                        href={arbiscanAddr(addr)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/45 hover:text-white transition-colors"
                        aria-label="View on Arbiscan"
                    >
                        <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
                    </a>
                </div>
            )}
        </div>
    );
};

/* -------- Sale status hook (reads live on-chain, no wallet required) -------- */
const useSaleState = () => {
    const sale = AIMA_CONFIG.contracts.aimaSale;
    const common = { address: sale, abi: AIMA_SALE_ABI, chainId: AIMA_CONFIG.chain.id };
    const startQ = useReadContract({ ...common, functionName: "saleStart" });
    const endQ = useReadContract({ ...common, functionName: "saleEnd" });
    const finQ = useReadContract({ ...common, functionName: "saleFinalized" });
    const pausedQ = useReadContract({ ...common, functionName: "paused" });
    const activeQ = useReadContract({ ...common, functionName: "isSaleActive" });

    const anyLoading = startQ.isLoading || endQ.isLoading || finQ.isLoading || pausedQ.isLoading || activeQ.isLoading;
    const anyError = startQ.isError && endQ.isError && finQ.isError && pausedQ.isError && activeQ.isError;

    const state = useMemo(() => {
        if (anyLoading) return { key: "loading", start: 0n, end: 0n };
        if (anyError) return { key: "unavailable", start: 0n, end: 0n };
        const start = startQ.data ?? 0n;
        const end = endQ.data ?? 0n;
        const finalized = !!finQ.data;
        const paused = !!pausedQ.data;
        const onchainActive = !!activeQ.data;
        const now = BigInt(Math.floor(Date.now() / 1000));
        let key;
        if (finalized) key = "finalized";
        else if (paused) key = "paused";
        else if (start === 0n || end === 0n) key = "not_active";
        else if (onchainActive) key = "active";
        else if (now < start) key = "scheduled";
        else if (now > end) key = "ended";
        else key = "not_active";
        return { key, start, end, finalized, paused, onchainActive };
    }, [anyLoading, anyError, startQ.data, endQ.data, finQ.data, pausedQ.data, activeQ.data]);

    const refetch = useCallback(() => {
        startQ.refetch(); endQ.refetch(); finQ.refetch(); pausedQ.refetch(); activeQ.refetch();
    }, [startQ, endQ, finQ, pausedQ, activeQ]);

    return { ...state, refetch };
};

/* -------- Sale stats hook -------- */
const useSaleStats = () => {
    const sale = AIMA_CONFIG.contracts.aimaSale;
    const common = { address: sale, abi: AIMA_SALE_ABI, chainId: AIMA_CONFIG.chain.id };
    const remainingPool = useReadContract({ ...common, functionName: "remainingSalePool" });
    const remainingRound = useReadContract({ ...common, functionName: "remainingRoundCap" });
    const totalSold = useReadContract({ ...common, functionName: "totalSold" });
    const usdcReceived = useReadContract({ ...common, functionName: "totalUSDCReceived" });
    const usdtReceived = useReadContract({ ...common, functionName: "totalUSDTReceived" });
    return {
        remainingPool: remainingPool.data,
        remainingRound: remainingRound.data,
        totalSold: totalSold.data,
        usdcReceived: usdcReceived.data,
        usdtReceived: usdtReceived.data,
    };
};

/* -------- Wallet connect controls -------- */
const WalletControls = () => {
    const { t } = useTranslation();
    const { address, isConnected } = useAccount();
    const chainId = useChainId();
    const { connectors, connect, isPending } = useConnect();
    const { disconnect } = useDisconnect();
    const { switchChain, isPending: switching } = useSwitchChain();
    const [menu, setMenu] = useState(false);
    const wrongNetwork = isConnected && chainId !== AIMA_CONFIG.chain.id;

    if (!isConnected) {
        const list = connectors.filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i);
        return (
            <div className="relative">
                <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    data-testid="wallet-connect-btn"
                    title={t("sale.status.not_active")}
                    className="group w-full h-14 bg-graphite text-cyan border border-cyan/60 font-plex text-lg md:text-xl font-semibold tracking-tight inline-flex items-center justify-center gap-3 opacity-60 cursor-not-allowed"
                >
                    <Wallet className="w-5 h-5" strokeWidth={1.7} />
                    {t("sale.buy.connect_wallet")}
                </button>
                {menu && (
                    <div className="absolute z-20 left-0 right-0 mt-2 border border-white/10 bg-graphite/95 backdrop-blur-md">
                        {list.map((c) => (
                            <button
                                key={c.uid || c.id}
                                type="button"
                                data-testid={`wallet-connector-${c.id}`}
                                onClick={() => { connect({ connector: c }); setMenu(false); }}
                                className="w-full h-12 px-4 text-left font-plex text-[13.5px] text-white/80 hover:bg-white/[0.04] hover:text-white transition-colors border-t border-white/[0.05] first:border-t-0"
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (wrongNetwork) {
        return (
            <button
                type="button"
                onClick={() => switchChain({ chainId: AIMA_CONFIG.chain.id })}
                disabled={switching}
                data-testid="wallet-switch-btn"
                className="group w-full h-14 bg-white text-black font-plex text-sm font-medium hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-60"
            >
                <AlertTriangle className="w-4 h-4" strokeWidth={1.7} />
                {switching ? t("sale.buy.processing") : t("sale.buy.wrong_network")}
            </button>
        );
    }

    return (
        <div className="flex items-center justify-between border border-white/[0.08] bg-black/30 px-4 py-3" data-testid="wallet-connected">
            <div className="font-mono text-[11px] text-white/75">
                {shortAddr(address)}
                <span className="ml-2 text-white/35">· Arbitrum One</span>
            </div>
            <button
                onClick={() => disconnect()}
                data-testid="wallet-disconnect-btn"
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-white transition-colors"
            >
                {t("sale.buy.disconnect")}
            </button>
        </div>
    );
};

/* -------- Eligibility checkboxes -------- */
const EligibilityGate = ({ items, onAllChecked }) => {
    const [state, setState] = useState(items.map(() => false));
    useEffect(() => {
        onAllChecked(state.every(Boolean));
    }, [state, onAllChecked]);
    return (
        <ul className="space-y-3">
            {items.map((label, i) => {
                const checked = state[i];
                return (
                    <li key={i}>
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <span
                                className={`mt-[3px] w-4 h-4 shrink-0 border transition-all flex items-center justify-center ${
                                    checked ? "bg-cyan border-cyan" : "border-white/25 group-hover:border-white/50"
                                }`}
                            >
                                {checked && <CheckCircle2 className="w-3 h-3 text-black" strokeWidth={2.5} />}
                            </span>
                            <input
                                type="checkbox"
                                className="sr-only"
                                checked={checked}
                                data-testid={`eligibility-${i}`}
                                onChange={(e) =>
                                    setState((s) => s.map((v, idx) => (idx === i ? e.target.checked : v)))
                                }
                            />
                            <span className="font-plex text-[13.5px] text-white/70 leading-relaxed group-hover:text-white/85 transition-colors">
                                {label}
                            </span>
                        </label>
                    </li>
                );
            })}
        </ul>
    );
};

/* -------- Buy panel -------- */
const BuyPanel = () => {
    const { t } = useTranslation();
    const { address, isConnected } = useAccount();
    const chainId = useChainId();
    const [payToken, setPayToken] = useState("USDC");
    const [amount, setAmount] = useState("100");
    const [allChecked, setAllChecked] = useState(false);
    const [step, setStep] = useState("idle"); // idle | approving | buying | success
    const [txHash, setTxHash] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const tokenCfg = AIMA_CONFIG.paymentTokens[payToken];
    const wrongNet = isConnected && chainId !== AIMA_CONFIG.chain.id;

    // Payment tokens use 6 decimals on Arbitrum One.
    const amountUnits = useMemo(() => {
        const n = parseFloat(amount || "0");
        if (!n || n <= 0) return 0n;
        try {
            return parseUnits(String(n), 6);
        } catch (_) {
            return 0n;
        }
    }, [amount]);

    // Live quote from the AIMASale contract.
    const quoteFn = payToken === "USDC" ? "quoteTokensForUSDC" : "quoteTokensForUSDT";
    const quoteQ = useReadContract({
        address: AIMA_CONFIG.contracts.aimaSale,
        abi: AIMA_SALE_ABI,
        functionName: quoteFn,
        args: [amountUnits],
        chainId: AIMA_CONFIG.chain.id,
        query: { enabled: amountUnits > 0n },
    });

    // Allowance for the selected payment token toward the AIMASale contract.
    const allowanceQ = useReadContract({
        address: tokenCfg.address,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address, AIMA_CONFIG.contracts.aimaSale],
        chainId: AIMA_CONFIG.chain.id,
        query: { enabled: !!address },
    });

    const needsApproval = (allowanceQ.data ?? 0n) < amountUnits;

    const { writeContractAsync } = useWriteContract();
    const receiptQ = useWaitForTransactionReceipt({
        hash: txHash,
        query: { enabled: !!txHash },
    });

    useEffect(() => {
        if (receiptQ.isSuccess && step === "buying") {
            setStep("success");
        }
    }, [receiptQ.isSuccess, step]);

    const parseErr = (e) => e?.shortMessage || e?.details || t("sale.buy.tx_rejected");

    const onApprove = async () => {
        setErrorMsg(null);
        try {
            setStep("approving");
            const hash = await writeContractAsync({
                address: tokenCfg.address,
                abi: ERC20_ABI,
                functionName: "approve",
                args: [AIMA_CONFIG.contracts.aimaSale, amountUnits],
            });
            setTxHash(hash);
            await new Promise((r) => setTimeout(r, 2500));
            allowanceQ.refetch();
            setStep("idle");
            setTxHash(null);
        } catch (e) {
            setErrorMsg(parseErr(e));
            setStep("idle");
        }
    };

    const onBuy = async () => {
        setErrorMsg(null);
        try {
            setStep("buying");
            const hash = await writeContractAsync({
                address: AIMA_CONFIG.contracts.aimaSale,
                abi: AIMA_SALE_ABI,
                functionName: payToken === "USDC" ? "buyWithUSDC" : "buyWithUSDT",
                args: [amountUnits],
            });
            setTxHash(hash);
        } catch (e) {
            setErrorMsg(parseErr(e));
            setStep("idle");
        }
    };

    const submitDisabled = !isConnected || wrongNet || !allChecked || amountUnits === 0n;

    return (
        <div className="border border-white/[0.08] bg-graphite overflow-hidden">
            <div className="flex items-center justify-between px-5 h-12 border-b border-white/[0.06] bg-black/30">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/75">
                    {t("sale.buy.title")}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan/80 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan" />
                    Arbitrum One
                </span>
            </div>

            <div className="p-5 md:p-6 space-y-5">
                {/* Pay-with */}
                <div>
                    <div className="eyebrow mb-2">{t("sale.buy.pay_with")}</div>
                    <div className="grid grid-cols-2 gap-px bg-white/[0.06]">
                        {["USDC", "USDT"].map((c) => (
                            <button
                                key={c}
                                onClick={() => setPayToken(c)}
                                data-testid={`pay-${c.toLowerCase()}`}
                                className={`relative h-11 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                    payToken === c
                                        ? "bg-[#11101a] text-white"
                                        : "bg-graphite text-white/45 hover:text-white/75"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Amount */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="eyebrow">{t("sale.buy.amount")}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
                            {payToken}
                        </span>
                    </div>
                    <div className="border border-white/[0.08] bg-black/40 focus-within:border-white/30 transition-colors">
                        <input
                            data-testid="buy-amount-input"
                            type="number"
                            min={0}
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full bg-transparent font-cabinet text-white text-2xl md:text-3xl tracking-tight px-4 py-4 outline-none placeholder:text-white/25"
                        />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
                            {t("sale.buy.min_max")}
                        </span>
                        <div className="flex gap-1">
                            {[100, 500, 1000, 5000].map((q) => (
                                <button
                                    key={q}
                                    onClick={() => setAmount(String(q))}
                                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-white border border-white/[0.06] hover:border-white/15 px-2 py-1 transition-colors"
                                >
                                    {q.toLocaleString()}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Receive */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="eyebrow">{t("sale.buy.receive")}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan/80">
                            AIMA
                        </span>
                    </div>
                    <div className="border border-white/[0.08] bg-black/30 px-4 py-4">
                        <div className="font-cabinet text-2xl md:text-3xl text-white tracking-tight" data-testid="quote-output">
                            {quoteQ.isSuccess && quoteQ.data !== undefined
                                ? fmt(quoteQ.data, 18, 4)
                                : "—"}
                        </div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
                            {t("sale.buy.quote_hint")}
                        </div>
                    </div>
                </div>

                {/* Wallet connect / switch */}
                <WalletControls />

                {/* Eligibility */}
                {isConnected && !wrongNet && (
                    <div className="border-t border-white/[0.06] pt-5">
                        <div className="eyebrow mb-1">{t("sale.eligibility.title")}</div>
                        <div className="font-plex text-[12.5px] text-white/45 mb-3">
                            {t("sale.eligibility.subtitle")}
                        </div>
                        <EligibilityGate
                            items={t("sale.eligibility.items", { returnObjects: true })}
                            onAllChecked={setAllChecked}
                        />
                    </div>
                )}

                {/* Action buttons */}
                {isConnected && !wrongNet && (
                    <div className="pt-1 space-y-3">
                        {needsApproval && (
                            <button
                                onClick={onApprove}
                                disabled={!allChecked || step === "approving" || amountUnits === 0n}
                                data-testid="approve-btn"
                                className="w-full h-14 border border-white/25 hover:border-white/50 text-white font-plex text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-3"
                            >
                                {step === "approving" && <Loader2 className="w-4 h-4 animate-spin" />}
                                {step === "approving"
                                    ? t("sale.buy.processing")
                                    : payToken === "USDC"
                                    ? t("sale.buy.approve_usdc")
                                    : t("sale.buy.approve_usdt")}
                            </button>
                        )}
                        <button
                            onClick={onBuy}
                            disabled={submitDisabled || needsApproval || step === "buying"}
                            data-testid="buy-btn"
                            className="w-full h-14 bg-white text-black font-plex text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
                        >
                            {step === "buying" && <Loader2 className="w-4 h-4 animate-spin" />}
                            {step === "buying" ? t("sale.buy.processing") : t("sale.buy.confirm")}
                        </button>
                    </div>
                )}

                {/* Error */}
                {errorMsg && (
                    <div className="border border-red-500/30 bg-red-500/[0.06] p-4 flex items-start gap-2" data-testid="buy-error">
                        <AlertTriangle className="w-4 h-4 mt-0.5 text-red-400 shrink-0" strokeWidth={1.8} />
                        <span className="font-plex text-[12.5px] text-red-200/80 leading-relaxed">{errorMsg}</span>
                    </div>
                )}

                {/* Success */}
                {step === "success" && txHash && (
                    <div className="border border-cyan/30 bg-cyan/[0.05] p-4" data-testid="buy-success">
                        <div className="flex items-center gap-2 text-cyan">
                            <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                                {t("sale.buy.success")}
                            </span>
                        </div>
                        <div className="mt-3 font-mono text-[11.5px] text-white/70 space-y-1">
                            <div>
                                {t("sale.buy.tx_hash")}: {" "}
                                <a
                                    href={arbiscanTx(txHash)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-cyan hover:underline break-all"
                                >
                                    {shortAddr(txHash)}
                                </a>
                            </div>
                            <div>{t("sale.buy.wallet")}: {shortAddr(address)}</div>
                            <div>{t("sale.buy.payment_token")}: {payToken}</div>
                            <div>{t("sale.buy.payment_amount")}: {amount} {payToken}</div>
                        </div>
                        <div className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/40">
                            {t("sale.buy.vesting_note")}
                        </div>
                    </div>
                )}

                <div className="pt-2 flex items-start gap-2 font-plex text-[12px] text-white/45 leading-relaxed border-t border-white/[0.06]">
                    <Info className="w-3.5 h-3.5 mt-[10px] shrink-0 text-white/40" strokeWidth={1.5} />
                    <span className="pt-2">
                        {t("sale.info.explanation")}
                    </span>
                </div>
            </div>
        </div>
    );
};

/* -------- Investor form modal (backend-connected) -------- */
const InvestorFormModal = ({ open, kind, onClose }) => {
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!open) { setSubmitted(false); setError(null); setSubmitting(false); }
    }, [open]);
    if (!open) return null;
    const isStrategic = kind === "strategic";
    const nsRoot = isStrategic ? "strategic" : "waitlist";
    const fields = isStrategic
        ? ["full_name", "company", "email", "country", "wallet", "amount", "token", "message"]
        : ["full_name", "email", "country", "wallet", "message"];

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        const fd = new FormData(e.target);
        const payload = { form_type: kind };
        fd.forEach((v, k) => { payload[k] = typeof v === "string" ? v.trim() : v; });
        setSubmitting(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(typeof body.detail === "string" ? body.detail : t("common.submit_error"));
            }
            setSubmitted(true);
        } catch (err) {
            setError(err.message || t("common.submit_error"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid={`${nsRoot}-modal`}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-[560px] bg-graphite border border-white/10 overflow-hidden my-8"
            >
                <div className="absolute -top-24 -right-24 w-64 h-64 glow-violet opacity-50 pointer-events-none" />
                <div className="relative p-7 md:p-9">
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <div className="eyebrow mb-3">{t(`${nsRoot}.title`)}</div>
                            <p className="font-plex text-white/60 text-[14px] max-w-md leading-relaxed">
                                {t(`${nsRoot}.sub`)}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid={`${nsRoot}-close`}
                            className="text-white/40 hover:text-white transition-colors"
                            aria-label={t("common.cancel")}
                        >
                            <X className="w-5 h-5" strokeWidth={1.8} />
                        </button>
                    </div>

                    {submitted ? (
                        <div className="py-8 text-center" data-testid={`${nsRoot}-success`}>
                            <CheckCircle2 className="w-8 h-8 text-cyan mx-auto mb-4" strokeWidth={1.5} />
                            <p className="font-plex text-white/80 text-[15px]">{t(`${nsRoot}.success`)}</p>
                            <button
                                onClick={onClose}
                                className="mt-6 h-10 px-5 border border-white/15 hover:border-white/35 text-white/85 font-plex text-sm transition-colors"
                            >
                                {t(`${nsRoot}.close`)}
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="space-y-4">
                            {fields.map((f) => (
                                <div key={f}>
                                    <label className="eyebrow block mb-1.5">
                                        {t(`${nsRoot}.fields.${f}`)}
                                    </label>
                                    {f === "message" ? (
                                        <textarea
                                            name={f}
                                            rows={3}
                                            data-testid={`${nsRoot}-${f}`}
                                            className="w-full bg-black/40 border border-white/10 focus:border-white/30 outline-none px-3 py-2.5 text-white text-[14px] font-plex"
                                        />
                                    ) : f === "token" ? (
                                        <select
                                            name={f}
                                            required
                                            data-testid={`${nsRoot}-${f}`}
                                            className="w-full bg-black/40 border border-white/10 focus:border-white/30 outline-none px-3 h-11 text-white text-[14px] font-plex"
                                        >
                                            <option value="">{t("common.select")}</option>
                                            <option value="USDC">USDC</option>
                                            <option value="USDT">USDT</option>
                                        </select>
                                    ) : (
                                        <input
                                            name={f}
                                            required={f !== "company"}
                                            type={f === "email" ? "email" : "text"}
                                            data-testid={`${nsRoot}-${f}`}
                                            className="w-full bg-black/40 border border-white/10 focus:border-white/30 outline-none px-3 h-11 text-white text-[14px] font-plex"
                                        />
                                    )}
                                </div>
                            ))}
                            {error && (
                                <div className="border border-red-500/30 bg-red-500/[0.06] px-3 py-2.5 flex items-start gap-2" data-testid={`${nsRoot}-error`}>
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-red-400 shrink-0" strokeWidth={1.8} />
                                    <span className="font-plex text-[12.5px] text-red-200/80">{error}</span>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid={`${nsRoot}-submit`}
                                className="w-full h-12 bg-white text-black font-plex text-sm font-medium hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-60"
                            >
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {submitting ? t("sale.buy.processing") : t(`${nsRoot}.submit`)}
                                {!submitting && <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

/* -------- Hashlock audit panel (full-width, below both columns) -------- */
const AUDIT_PAGE_URL = "https://hashlock.com/audits/aima";

const AuditCard = () => {
    const { t } = useTranslation();
    return (
        <div className="border border-white/[0.08] bg-graphite px-6 py-6 md:px-8 md:py-7" data-testid="audit-card">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-10">
                {/* Brand + eyebrow */}
                <div className="shrink-0 flex items-center justify-between gap-4 lg:flex-col lg:items-start lg:justify-center lg:gap-3">
                    <img
                        src="/brand/hashlock_logo.svg"
                        alt="Hashlock"
                        className="h-5 lg:h-6 w-auto"
                        data-testid="audit-hashlock-logo"
                    />
                    <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">
                        {t("sale.audit.eyebrow")}
                    </div>
                </div>

                {/* Text block */}
                <div className="min-w-0 flex-1 lg:border-l lg:border-white/[0.06] lg:pl-10">
                    <div className="font-cabinet font-bold text-white text-[19px] md:text-[21px] tracking-tight leading-[1.2]">
                        {t("sale.audit.title")}
                    </div>
                    <p className="mt-2 font-plex text-[15px] md:text-base text-white/80 leading-[1.65] max-w-[62ch]">
                        {t("sale.audit.body")}
                    </p>
                    <div className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/40">
                        {t("sale.audit.meta")}
                    </div>
                </div>

                {/* Rating + report link */}
                <div className="shrink-0 flex flex-col gap-3 border-t border-white/[0.06] pt-5 lg:border-t-0 lg:pt-0 lg:items-end">
                    <div className="flex items-center gap-2.5" data-testid="audit-rating">
                        <ShieldCheck className="w-4 h-4 text-cyan" strokeWidth={1.7} />
                        <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-cyan font-semibold">
                            {t("sale.audit.rating_label")} {t("sale.audit.rating_value")}
                        </span>
                    </div>
                    <a
                        href={AUDIT_PAGE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="audit-report-link"
                        className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-cyan font-semibold hover:text-white transition-colors"
                    >
                        {t("sale.audit.cta")}
                        <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                    </a>
                </div>
            </div>
        </div>
    );
};

/* -------- Sale stats grid -------- */
const SaleStats = () => {
    const { t } = useTranslation();
    const s = useSaleStats();
    const items = [
        { k: t("sale.stats.remaining_pool"), v: fmt(s.remainingPool, 18, 0), u: "AIMA" },
        { k: t("sale.stats.remaining_round"), v: fmt(s.remainingRound, 18, 0), u: "AIMA" },
        { k: t("sale.stats.total_sold"), v: fmt(s.totalSold, 18, 0), u: "AIMA" },
        { k: t("sale.stats.usdc_received"), v: fmt(s.usdcReceived, 6, 0), u: "USDC" },
        { k: t("sale.stats.usdt_received"), v: fmt(s.usdtReceived, 6, 0), u: "USDT" },
    ];
    return (
        <div className="border border-white/[0.08] bg-graphite" data-testid="sale-stats">
            <div className="flex items-center justify-between px-5 h-12 border-b border-white/[0.06]">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/55">
                    {t("sale.stats.label")}
                </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/[0.06]">
                {items.map((it) => (
                    <div key={it.k} className="bg-graphite p-4">
                        <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/40 leading-relaxed">
                            {it.k}
                        </div>
                        <div className="mt-1.5 font-cabinet text-white text-lg tracking-tight">
                            {it.v}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/30">{it.u}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* -------- Section -------- */
export const RestrictedTokenSale = () => {
    const { t } = useTranslation();
    const sale = useSaleState();
    const [openForm, setOpenForm] = useState(null); // "waitlist" | "strategic" | null
    const [contractsOpen, setContractsOpen] = useState(false);
    const saleActive = sale.key === "active";

    const statusColor =
        sale.key === "active"
            ? "text-cyan border-cyan/40 bg-cyan/[0.05]"
            : sale.key === "finalized" || sale.key === "ended"
            ? "text-violet-soft border-violet/40 bg-violet/[0.05]"
            : sale.key === "paused"
            ? "text-amber-300 border-amber-400/40 bg-amber-400/[0.05]"
            : "text-white/70 border-white/15 bg-white/[0.02]";

    return (
        <section
            id="token-sale"
            data-testid="token-sale-section"
            className="relative section-y border-t border-white/[0.06] overflow-hidden"
        >
            <AmbientBackground variant="grid" opacity={0.7} />

            <div className="relative container-x max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:items-start lg:items-end mb-12">
                    <Reveal className="md:col-span-7">
                        <h2 className="h-display text-4xl md:text-5xl lg:text-[64px] text-white leading-[0.95]">
                            <span className="text-gradient-soft">{t("sale.title_line1")}</span>
                            <br />
                            <span className="text-gradient-accent">{t("sale.title_line2")}</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="md:col-span-5">
                        <p className="font-plex text-white/80 text-lg md:text-[19px] leading-[1.55] max-w-[46ch]">
                            {t("sale.intro")}
                        </p>
                    </Reveal>
                </div>

                {/* Status box */}
                <Reveal y={20}>
                    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 border ${statusColor} px-5 py-4 mb-8`} data-testid="sale-status">
                        <div className="flex items-center gap-3">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-cyan/60 animate-pulse-soft" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan" />
                            </span>
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 font-semibold">
                                {t("sale.status.label")}
                            </span>
                            <span className="font-cabinet font-bold tracking-tight text-cyan text-[19px] md:text-[21px]" data-testid="sale-status-value">
                                {t(`sale.status.${sale.key}`)}
                            </span>
                        </div>
                        <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-cyan font-semibold">
                            Chain {AIMA_CONFIG.chain.id} · Arbitrum One
                        </span>
                    </div>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
                    {/* LEFT — contract info + stats + eligibility explanations */}
                    <div className="lg:col-span-7 space-y-6">
                        <Reveal y={20}>
                            <SaleStats />
                        </Reveal>

                        <Reveal y={20}>
                            <div className="border border-white/[0.08] bg-graphite">
                                <button
                                    type="button"
                                    onClick={() => setContractsOpen((v) => !v)}
                                    aria-expanded={contractsOpen}
                                    data-testid="contracts-toggle"
                                    className="w-full flex items-center justify-between px-5 h-12 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors"
                                >
                                    <span className="flex items-center gap-3">
                                        <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/70 font-semibold">
                                            {t("sale.info.label")}
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 hidden sm:inline">
                                            {t("sale.info.network")} · Chain {AIMA_CONFIG.chain.id}
                                        </span>
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 text-white/50 transition-transform ${contractsOpen ? "rotate-180" : ""}`}
                                        strokeWidth={1.8}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {contractsOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-5">
                                                <AddressRow label={t("sale.info.aima_sale")} addr={AIMA_CONFIG.contracts.aimaSale} pendingLabel={t("sale.info.pending_audit")} />
                                                <AddressRow label={t("sale.info.aima_token")} addr={AIMA_CONFIG.contracts.aimaToken} pendingLabel={t("sale.info.pending_audit")} />
                                                <AddressRow label={t("sale.info.aima_vesting")} addr={AIMA_CONFIG.contracts.aimaVesting} pendingLabel={t("sale.info.pending_audit")} />
                                                <AddressRow label={t("sale.info.treasury")} addr={AIMA_CONFIG.contracts.safeTreasury} treasury />
                                                <AddressRow label={t("sale.info.usdc")} addr={AIMA_CONFIG.paymentTokens.USDC.address} />
                                                <AddressRow label={t("sale.info.usdt")} addr={AIMA_CONFIG.paymentTokens.USDT.address} />
                                            </div>
                                            <div className="px-5 py-4 border-t border-white/[0.06] bg-black/20">
                                                <p className="font-plex text-[13px] text-white/70 leading-relaxed">
                                                    {t("sale.info.explanation")}
                                                </p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </Reveal>

                        {/* Security notice — directly below Contract Information */}
                        <Reveal y={20}>
                            <div className="border border-white/[0.08] bg-graphite p-6 md:p-7 flex gap-5">
                                <div className="inline-flex items-center justify-center w-11 h-11 shrink-0 border border-cyan/40 bg-cyan/[0.05]">
                                    <ShieldAlert className="w-5 h-5 text-cyan" strokeWidth={1.7} />
                                </div>
                                <div>
                                    <div className="font-cabinet font-bold text-white text-[19px] md:text-[21px] tracking-tight leading-[1.2]">
                                        {t("sale.security.title")}
                                    </div>
                                    <p className="mt-2.5 font-plex text-[15px] md:text-base text-white/80 leading-[1.65]">
                                        {t("sale.security.body")}
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* RIGHT — buy widget OR inactive block */}
                    <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
                        {saleActive ? (
                            <Reveal y={20}>
                                <BuyPanel />
                            </Reveal>
                        ) : (
                            <Reveal y={20}>
                                <div className="border border-white/[0.08] bg-graphite overflow-hidden">
                                    <div className="relative">
                                        <div className="absolute -top-20 -right-20 w-64 h-64 glow-violet opacity-50 pointer-events-none" />
                                        <div className="absolute -bottom-20 -left-20 w-64 h-64 glow-cyan opacity-30 pointer-events-none" />
                                        <div className="relative p-6 md:p-8">
                                            <h3 className="font-cabinet text-white text-2xl md:text-3xl tracking-tight leading-tight" data-testid="sale-inactive-title">
                                                {t(`sale.status.${sale.key}`)}
                                            </h3>
                                            <p className="mt-4 font-plex text-[15px] md:text-base text-white/80 leading-[1.65]">
                                                {t("sale.inactive.body")}
                                            </p>
                                            {/* Buy button intentionally disabled while sale is not active on-chain */}
                                            <button
                                                type="button"
                                                disabled
                                                data-testid="buy-btn-disabled"
                                                aria-disabled="true"
                                                className="mt-6 w-full h-12 bg-white/10 text-white/40 font-plex text-sm font-medium cursor-not-allowed inline-flex items-center justify-center gap-3"
                                            >
                                                {t("sale.buy.confirm")}
                                            </button>
                                            <div className="mt-6 space-y-3">
                                                <button
                                                    onClick={() => setOpenForm("waitlist")}
                                                    data-testid="open-waitlist"
                                                    className="w-full h-12 bg-graphite text-cyan border border-cyan/60 font-plex text-sm font-medium transition-all hover:bg-cyan/[0.08] hover:border-cyan hover:shadow-[0_0_28px_-6px_rgba(0,229,255,0.55)] inline-flex items-center justify-center gap-3"
                                                >
                                                    {t("sale.inactive.cta_waitlist")}
                                                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                                                </button>
                                                <button
                                                    onClick={() => setOpenForm("strategic")}
                                                    data-testid="open-strategic"
                                                    className="w-full h-12 border border-white/15 hover:border-white/35 text-white font-plex text-sm transition-colors inline-flex items-center justify-center gap-3"
                                                >
                                                    {t("sale.inactive.cta_strategic")}
                                                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="border border-white/[0.06] bg-graphite p-5">
                                    <WalletControls />
                                </div>
                            </Reveal>
                        )}

                        {/* Strategic investor CTA always visible when active */}
                        {saleActive && (
                            <Reveal y={20}>
                                <button
                                    onClick={() => setOpenForm("strategic")}
                                    data-testid="strategic-inline-cta"
                                    className="w-full h-12 border border-white/15 hover:border-white/35 text-white font-plex text-sm transition-colors inline-flex items-center justify-center gap-3"
                                >
                                    {t("sale.inactive.cta_strategic")}
                                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                                </button>
                            </Reveal>
                        )}

                    </div>
                </div>

                {/* Hashlock audit — full-width panel below both columns, aligned with the Sale Status bar */}
                <Reveal y={20} className="mt-6 lg:mt-10">
                    <AuditCard />
                </Reveal>
            </div>

            <AnimatePresence>
                {openForm && (
                    <InvestorFormModal
                        open={!!openForm}
                        kind={openForm}
                        onClose={() => setOpenForm(null)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default RestrictedTokenSale;
